import { action, makeObservable, observable } from 'mobx';
import { betStore } from '@pascal/pascal-core/shared/core/store';
import { BetResponseCode, BetStatus } from '@pascal/pascal-core';

import { WheelTypes } from '../enums';
import { gameService } from '../services';
import { socketEventsHandlerDelays } from '../enums/replaySocetEventsDelays';

export const wheelDeviation = 5.5;
export const multiplierDelay = 1000;

class GameStore {
  isLiveBetsLoader = false;

  betsClosedDuration = null;

  time = null;

  isBetsClosed = false;

  isFakeImagesLoaded = false;

  isLoadingPage = true;

  gameAnimationDuration = 1500;

  timerDuration = 10;

  mobileGameScene = null;

  isPongReceived = false;

  gameState = {
    status: '',
    second: null,
    isInitialLoaded: false,
  };

  constructor() {
    makeObservable(this, {
      time: observable,
      gameState: observable,
      isLoadingPage: observable,
      isBetsClosed: observable,
      isPongReceived: observable,
      timerDuration: observable,
      mobileGameScene: observable,
      isLiveBetsLoader: observable,
      betsClosedDuration: observable,
      isFakeImagesLoaded: observable,
      gameAnimationDuration: observable,
      setTime: action.bound,
      setGameState: action.bound,
      setIsBetsClosed: action.bound,
      setMobileGameScene: action.bound,
      setIsLoadingPage: action.bound,
      setTimerDuration: action.bound,
      setIsPongReceived: action.bound,
      setIsLiveBetsLoader: action.bound,
      setBetsClosedDuration: action.bound,
      updateIsFakeImagesLoaded: action.bound,
      setGameAnimationDuration: action.bound,
      handleReplayGame: action.bound,
      handleReplayResultReport: action.bound,
      handleReplayRoundFin: action.bound,
    });
  }

  setTime(time) {
    this.time = time;
  }

  setBetsClosedDuration(betsClosedDuration) {
    this.betsClosedDuration = betsClosedDuration;
  }

  setMobileGameScene(mobileGameScene) {
    this.mobileGameScene = mobileGameScene;
  }

  setIsLoadingPage(isLoadingPage) {
    this.isLoadingPage = isLoadingPage;
  }

  setIsBetsClosed(isBetsClosed) {
    this.isBetsClosed = isBetsClosed;
  }

  setIsLiveBetsLoader(isLiveBetsLoader) {
    this.isLiveBetsLoader = isLiveBetsLoader;
  }

  updateIsFakeImagesLoaded(fakeImagesLoadedCount, fakeImagesCount) {
    this.isFakeImagesLoaded = fakeImagesLoadedCount === fakeImagesCount;
  }

  setGameAnimationDuration(gameAnimationDuration) {
    this.gameAnimationDuration = gameAnimationDuration;
  }

  setTimerDuration(timeValue) {
    this.timerDuration = timeValue;
  }

  setGameState(status) {
    this.gameState = status;
  }

  setIsPongReceived(isPongReceived) {
    this.isPongReceived = isPongReceived;
  }

  handleReplayResultReport(repliedBetData, second, wheelStatus) {
    setTimeout(() => {
      gameService.handleResultReport({
        data: {
          resultReportData: {
            multiplierCandy: {
              multiplierCandy: repliedBetData.roundData.multiplierCandy,
              multiplierOdd: repliedBetData.roundData.multiplierOdd,
            },
            wheel: wheelStatus,
            wheelResult: {
              index: repliedBetData.roundData.wheelIndexes[wheelStatus],
              candy: repliedBetData.roundData.candy,
            },
          },
        },
      });
    }, second);
  }

  handleReplayRoundFin(repliedBetData, second) {
    setTimeout(() => {
      gameService.handleRoundFin({
        data: {
          bets: betStore.liveBets,
          finishedRound: {
            roundData: {
              candy: repliedBetData.roundData.candy,
              odd: repliedBetData.roundData.odd,
              multiplierOdd: repliedBetData.roundData.multiplierOdd,
              multiplierCandy: repliedBetData.roundData.multiplierCandy,
              wheelState: repliedBetData.roundData.wheelState,
              wheelIndexes: repliedBetData.roundData.wheelIndexes,
            },
          },
        },
      });
    }, second);
  }

  handleReplayGame() {
    const { repliedBetData } = betStore;
    let firstWheelCircle;
    let secondWheelCircle;
    let thirdWheelCircle;

    if (repliedBetData) {
      setTimeout(() => {
        gameService.handleRoundStart({
          data: {
            startedRound: {
              roundId: repliedBetData.roundId,
              duration: 10,
            },
          },
        });
      }, socketEventsHandlerDelays.roundStartDelay);

      setTimeout(() => {
        gameService.handleDoBetResult({
          ...repliedBetData,
          status: BetStatus.Pending,
          betData: repliedBetData.additionalParams.map((item) => ({
            ...item,
            status: BetStatus.Pending,
            winAmount: 0,
          })),
          responseCode: BetResponseCode.Success,
        });
      }, socketEventsHandlerDelays.doBetResultDelay);

      setTimeout(() => {
        gameService.handleBetsClosed();
      }, socketEventsHandlerDelays.betsClosedDelay);

      this.handleReplayResultReport(
        repliedBetData,
        socketEventsHandlerDelays.firstResultReportDelay,
        WheelTypes.first
      );

      firstWheelCircle = true;

      if (repliedBetData.roundData.wheelIndexes[1] && firstWheelCircle) {
        if (
          repliedBetData.roundData.wheelIndexes[0] === 1 ||
          repliedBetData.roundData.wheelIndexes[0] === 16
        ) {
          secondWheelCircle = true;
          this.handleReplayResultReport(
            repliedBetData,
            socketEventsHandlerDelays.secondResultReportDelay,
            WheelTypes.second
          );
        } else {
          this.handleReplayRoundFin(
            repliedBetData,
            socketEventsHandlerDelays.secondResultReportDelay
          );
        }
      }

      if (repliedBetData.roundData.wheelIndexes[2] && secondWheelCircle) {
        if (
          repliedBetData.roundData.wheelIndexes[1] === 1 ||
          repliedBetData.roundData.wheelIndexes[1] === 11 ||
          repliedBetData.roundData.wheelIndexes[1] === 21
        ) {
          thirdWheelCircle = true;
          this.handleReplayResultReport(
            repliedBetData,
            socketEventsHandlerDelays.thirdResultReportDelay,
            WheelTypes.third
          );
        } else {
          this.handleReplayRoundFin(
            repliedBetData,
            socketEventsHandlerDelays.thirdResultReportDelay
          );
        }

        if (thirdWheelCircle) {
          this.handleReplayRoundFin(
            repliedBetData,
            socketEventsHandlerDelays.roundFinDelay +
              socketEventsHandlerDelays.thirdResultReportDelay
          );
        }
      }
    }
  }
}

export const gameStore = new GameStore();
